import { useEffect } from "react";
import { z } from "zod";

import { WarningCircle as WarningCircleIcon } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { AuthApiFactory } from "@/apis/auth";
import { useSubscriptionStore } from "@/store/subscription";
import { useInitializeSession } from "@/hooks/use-initialize";
import { useAuthenticationStore } from "@/store/authentication";

export const Route = createFileRoute("/auth/_layout/callback/magic-link")({
  validateSearch: z.object({ code: z.string().nullish() }),
  component: RouteComponent,
});

const UnpaidSchema = z.object({
  message: z.string().email(),
  error_code: z.literal("USER_NOT_PAID"),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const params = Route.useSearch();

  const authenticate = useAuthenticationStore((store) => store.authenticate);
  const setSubscription = useSubscriptionStore((store) => store.setSubscription);
  const initializeSession = useInitializeSession();

  const {
    mutate: verifyMagicLink,
    status,
    error,
  } = useMutation({
    mutationFn: (code: string) => {
      return AuthApiFactory.Apis.VerifyMagicLink(code);
    },
    onSuccess: async () => {
      const session = await AuthApiFactory.Apis.Authenticate();
      const initialize = await initializeSession();
      authenticate(session.data.user, session.data.organization, initialize.profile);
      setSubscription(initialize.subscription);
    },
    onError: (error) => {
      const unpaid = UnpaidSchema.safeParse(error);
      if (unpaid.success) {
        navigate({ to: "/auth", search: { plans: true, email: unpaid.data.message } });
      } else {
        setTimeout(() => navigate({ to: "/auth" }), 3000);
      }
    },
  });

  useEffect(() => {
    if (params.code) {
      verifyMagicLink(params.code);
    } else {
      setTimeout(() => navigate({ to: "/auth" }), 3000);
    }
  }, [params.code, verifyMagicLink, navigate]);

  if (!params.code) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center">
        <WarningCircleIcon className="size-8 text-destructive" />
        <p className="text-base font-medium text-foreground mt-3">Failed to login</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-96">We received an invalid code. You will be redirected back in 3 seconds...</p>
      </div>
    );
  }

  switch (status) {
    case "error":
      return (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <WarningCircleIcon className="size-8 text-destructive" />
          <p className="text-base font-medium text-foreground mt-3">Failed to login</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-96">{!error || !error.message ? "Oops! An error occurred" : error.message}. You will be redirected back in 3 seconds...</p>
        </div>
      );

    default:
      return (
        <div className="flex items-center justify-center gap-3 h-full">
          <div className="spinner size-6 !border-foreground" />
          <p className="text-sm text-foreground">Verifying magic link...</p>
        </div>
      );
  }
}
